import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Header } from '../ui';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Initialize Stripe (will use the publishable key from env)
let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

function PaymentStep({ data, updateData, completeOnboarding }) {
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [selectedCurrency, setSelectedCurrency] = useState('GBP');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const planData = {
    GBP: {
      monthly: {
        id: 'monthly',
        name: 'Monthly Plan',
        price: 8.00,
        interval: 'month',
        description: 'Perfect for getting started',
        savings: null,
        currency: '£',
        currencyCode: 'GBP'
      },
      quarterly: {
        id: 'quarterly', 
        name: 'Quarterly Plan',
        price: 18.00,
        interval: 'quarter',
        description: 'Best value - save 25%',
        savings: '25%',
        monthlyEquivalent: 6.00,
        currency: '£',
        currencyCode: 'GBP'
      }
    },
    USD: {
      monthly: {
        id: 'monthly',
        name: 'Monthly Plan',
        price: 11.00,
        interval: 'month',
        description: 'Perfect for getting started',
        savings: null,
        currency: '$',
        currencyCode: 'USD'
      },
      quarterly: {
        id: 'quarterly', 
        name: 'Quarterly Plan',
        price: 25.00,
        interval: 'quarter',
        description: 'Best value - save 24%',
        savings: '24%',
        monthlyEquivalent: 8.33,
        currency: '$',
        currencyCode: 'USD'
      }
    }
  };

  const plans = planData[selectedCurrency];

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    updateData({ selectedPlan: planId });
  };

  const handleStartTrial = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create checkout session on backend
      const response = await fetch(`${baseURL}/api/payment/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          planType: selectedPlan,
          currency: selectedCurrency,
          userEmail: data.email,
          userData: {
            firstName: data.firstName,
            lastName: data.lastName,
            employmentStatus: data.employmentStatus,
            // Fix job search status mapping: dashes to underscores
            jobSearchStatus: data.jobSearchStatus?.replace(/-/g, '_'),
            interestedRoleKeywords: data.roleKeywords,
            currentSkills: data.currentSkills,
            softwareFamiliarity: data.softwareFamiliarity,
            linkedinUrl: data.professionalLinks?.linkedin,
            githubUrl: data.professionalLinks?.github,
            personalWebsiteUrl: data.professionalLinks?.website
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Backend response error:', errorData);
        throw new Error(`Backend error: ${errorData}`);
      }

      const { sessionId, error: backendError } = await response.json();
      console.log('Backend response:', { sessionId, backendError });

      if (backendError) {
        throw new Error(backendError);
      }

      if (!sessionId) {
        throw new Error('No session ID received from backend');
      }

      // Redirect to Stripe Checkout
      console.log('Initializing Stripe with session ID:', sessionId);
      const stripe = await getStripe();
      
      if (!stripe) {
        throw new Error('Stripe failed to initialize. Check your publishable key.');
      }

      console.log('Redirecting to Stripe Checkout...');
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId
      });

      if (stripeError) {
        console.error('Stripe redirect error:', stripeError);
        throw new Error(stripeError.message);
      }

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 p-8 md:p-12">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-3">
          Start your <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">free trial</span>
        </h2>
        <p className="text-gray-400 text-lg">
          Choose your plan and begin your 7-day free trial
        </p>
        <div className="inline-flex items-center mt-4 px-4 py-2 bg-green-900 bg-opacity-30 border border-green-500 border-opacity-30 rounded-full">
          <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-green-300 font-medium">7 days free, then billing starts</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Currency Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-700 bg-opacity-50 rounded-lg p-1 flex">
            <button
              onClick={() => setSelectedCurrency('GBP')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                selectedCurrency === 'GBP'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              🇬🇧 GBP (£)
            </button>
            <button
              onClick={() => setSelectedCurrency('USD')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                selectedCurrency === 'USD'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              🇺🇸 USD ($)
            </button>
          </div>
        </div>

        {/* Plan Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {Object.values(plans).map((plan) => (
            <div
              key={plan.id}
              onClick={() => handlePlanSelect(plan.id)}
              className={`relative p-6 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                selectedPlan === plan.id
                  ? 'border-blue-500 bg-blue-500 bg-opacity-10'
                  : 'border-gray-600 bg-gray-700 bg-opacity-30 hover:border-gray-500'
              }`}
            >
              {/* Savings Badge */}
              {plan.savings && (
                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                  SAVE {plan.savings}
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                                    <div className="mb-2">
                      <span className="text-3xl font-bold text-white">{plan.currency}{plan.price}</span>
                      <span className="text-gray-400">/{plan.interval}</span>
                    </div>
                    {plan.monthlyEquivalent && (
                      <div className="text-sm text-green-400">
                        ~{plan.currency}{plan.monthlyEquivalent}/month
                      </div>
                    )}
                <p className="text-gray-300 text-sm mt-2">{plan.description}</p>
              </div>

              {/* Features */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center text-gray-300">
                  <svg className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  7-day free trial
                </div>
                <div className="flex items-center text-gray-300">
                  <svg className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  AI resume analysis & optimization
                </div>
                <div className="flex items-center text-gray-300">
                  <svg className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Track up to 50 job applications
                </div>
                <div className="flex items-center text-gray-300">
                  <svg className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Upload up to 8 CV versions
                </div>
                <div className="flex items-center text-gray-300">
                  <svg className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Personalized job insights
                </div>
                <div className="flex items-center text-gray-300">
                  <svg className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Cancel anytime
                </div>
              </div>

              {/* Selection Indicator */}
              {selectedPlan === plan.id && (
                <div className="absolute top-4 right-4">
                  <svg className="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-500 rounded-lg text-red-200">
            {error}
          </div>
        )}

        {/* Start Trial Button */}
        <div className="text-center">
          <button
            onClick={handleStartTrial}
            disabled={loading}
            className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold text-lg rounded-lg hover:from-blue-600 hover:to-purple-600 transform hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin w-5 h-5 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Starting your trial...
              </div>
            ) : (
              `Start Free Trial - ${plans[selectedPlan].name}`
            )}
          </button>

          <div className="mt-4 space-y-2 text-sm text-gray-400">
            <p>
              ✓ Free for 7 days, then {plans[selectedPlan].currency}{plans[selectedPlan].price}/{plans[selectedPlan].interval}
            </p>
            <p>
              ✓ Cancel anytime from your account settings
            </p>
            <p className="text-xs">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>

        {/* Security Badge */}
        <div className="mt-8 flex items-center justify-center text-gray-400">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span className="text-sm">Secured by Stripe</span>
        </div>
      </div>
    </div>
  );
}

export default PaymentStep; 